import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { env } from './env';
import { useSession } from './session';

/**
 * Real-time dashboard (migration 0098) — « le statut se met à jour tout seul ».
 *
 * Opens ONE Server-Sent-Events stream (`GET /merchant/events`) for the active shop and, on every change the server
 * reports (a new order, an order status, a parcel status from Jibex — webhook or polling), refetches the screens that
 * show it. Nothing is read from the event itself except WHICH lists are stale: data always comes from the normal,
 * RLS-scoped routes.
 *
 * `fetch` rather than `EventSource`: the stream needs the `Authorization` and `X-Shop-Id` headers, which
 * `EventSource` cannot send (and a token in the URL would end up in logs). It reconnects by itself (backoff 2 s → 30 s),
 * restarts with the new token when the session refreshes, and while it is down the open screens are refetched every
 * 30 s instead, so the merchant never has to press « Actualiser ».
 */
export type LiveStatus = 'connecting' | 'live' | 'offline';

const FALLBACK_REFRESH_MS = 30_000;
const DEBOUNCE_MS = 400;

interface ChangeEvent {
  readonly kind: 'order' | 'shipment';
}

/** Every query a change can make stale. Keys are prefixes: `['orders', shop]` covers each page and filter. */
function staleKeys(shopId: string, kinds: ReadonlySet<ChangeEvent['kind']>): unknown[][] {
  const keys: unknown[][] = [
    ['orders', shopId],
    ['merchant-analytics', shopId],
    ['deliveries-pending'],
  ];
  if (kinds.has('shipment')) keys.push(['shipments', shopId]);
  if (kinds.has('order'))
    keys.push(['shipments', shopId], ['invoices', shopId], ['products', shopId]);
  return keys;
}

/** Parses SSE frames out of a growing text buffer; returns the unconsumed tail. */
export function parseSseFrames(
  buffer: string,
  onFrame: (event: string, data: string) => void,
): string {
  const frames = buffer.split(/\r?\n\r?\n/u);
  const rest = frames.pop() ?? '';
  for (const frame of frames) {
    let event = 'message';
    const data: string[] = [];
    for (const line of frame.split(/\r?\n/u)) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
    }
    if (data.length > 0) onFrame(event, data.join('\n'));
  }
  return rest;
}

export function useLiveUpdates(): LiveStatus {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');

  useEffect(() => {
    if (accessToken === null || activeShopId === null) return undefined;
    const shopId = activeShopId;
    let stopped = false;
    let controller: AbortController | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let fallbackTimer: ReturnType<typeof setInterval> | undefined;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = 2000;
    let connectedOnce = false;
    const pending = new Set<ChangeEvent['kind']>();

    const refresh = (kinds: ReadonlySet<ChangeEvent['kind']>): void => {
      for (const queryKey of staleKeys(shopId, kinds))
        void queryClient.invalidateQueries({ queryKey });
    };
    const schedule = (kind: ChangeEvent['kind']): void => {
      pending.add(kind);
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const kinds = new Set(pending);
        pending.clear();
        refresh(kinds);
      }, DEBOUNCE_MS);
    };
    const startFallback = (): void => {
      if (fallbackTimer !== undefined) return;
      fallbackTimer = setInterval(() => {
        if (document.visibilityState === 'visible') refresh(new Set(['order', 'shipment']));
      }, FALLBACK_REFRESH_MS);
    };
    const stopFallback = (): void => {
      clearInterval(fallbackTimer);
      fallbackTimer = undefined;
    };

    const connect = async (): Promise<void> => {
      controller = new AbortController();
      setStatus('connecting');
      try {
        const response = await fetch(`${env.apiBaseUrl}/api/v1/merchant/events`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-Shop-Id': shopId,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!response.ok || response.body === null)
          throw new Error(`stream refused (${String(response.status)})`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer = parseSseFrames(
            buffer + decoder.decode(value, { stream: true }),
            (event, data) => {
              if (event === 'ready') {
                setStatus('live');
                stopFallback();
                retryDelay = 2000;
                // A (re)connection may have missed changes: one refetch brings every screen up to date.
                if (connectedOnce) refresh(new Set(['order', 'shipment']));
                connectedOnce = true;
              } else if (event === 'resync') {
                refresh(new Set(['order', 'shipment']));
              } else if (event === 'change') {
                try {
                  const change = JSON.parse(data) as Partial<ChangeEvent>;
                  if (change.kind === 'order' || change.kind === 'shipment') schedule(change.kind);
                } catch {
                  // A malformed frame is ignored; the next one (or the fallback) catches up.
                }
              }
            },
          );
        }
      } catch {
        // Network error, abort or a refused stream: handled below like a normal end.
      }
      if (stopped) return;
      setStatus('offline');
      startFallback();
      refresh(new Set(['order', 'shipment']));
      retryTimer = setTimeout(() => {
        void connect();
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, 30_000);
    };

    void connect();
    return () => {
      stopped = true;
      controller?.abort();
      clearTimeout(retryTimer);
      clearTimeout(debounceTimer);
      stopFallback();
    };
  }, [accessToken, activeShopId, queryClient]);

  return status;
}
